import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '../button';
import Icon from '../icon';
import useLocalStorage from '~/modules/hooks/use-localstorage';
import {
	Prompt, PromptBody, PromptDismiss, PromptText, PromptTitle
} from './notifications-panel.css';

export const DISMISSED_KEY = 'desktopNotificationsPromptDismissed';

function DesktopNotificationsPrompt(props) {
	const {
		permission,
		isSupported = false,
		isRequesting = false,
		onEnable
	} = props;

	// What the permission was when they waved us off, rather than a bare "yes".
	// A dismissal answers the situation in front of them — blocked, or not yet
	// asked — and stops meaning anything once that situation changes. Kept
	// sticky, resetting the permission in site settings left the panel silent
	// forever, with nothing anywhere offering to turn notifications back on.
	const [dismissedAt, setDismissedAt] = useLocalStorage(DISMISSED_KEY, null);

	// Dismissals used to be recorded as a bare `true`, and plenty of people are
	// still carrying one. There is no way to tell which permission it answered,
	// so read it against whatever the permission is now: still dismissed today,
	// and free to come back later, like every dismissal made since.
	const isLegacyDismissed = dismissedAt === true;
	const isDismissed = isLegacyDismissed || dismissedAt === permission;

	useEffect(() => {
		if( isLegacyDismissed ) {
			setDismissedAt(permission);
			return;
		}

		// A dismissal is spent the moment the user leaves the state it answered.
		// Keeping it would let a permission that comes back round — granted, then
		// reset to unasked — match an answer given about a different situation,
		// which is the silence this whole change exists to end.
		if( dismissedAt !== null && dismissedAt !== permission ) {
			setDismissedAt(null);
		}
	}, [isLegacyDismissed, dismissedAt, permission]);

	const handleEnable = async() => {
		const result = await onEnable?.();

		// A permission still 'default' after the ask means the user closed the
		// browser's own dialog without deciding. Browsers count repeat prompts
		// against the site, so treat that as the answer it is and stop offering
		// rather than re-arming the button.
		if( result === 'default' ) {
			setDismissedAt(result);
		}
	};

	// Nothing to ask for: the browser can't do it, we already have it, or the
	// user told us to stop asking about this particular state.
	if( !isSupported || permission === 'granted' || isDismissed ) {
		return null;
	}

	const isBlocked = permission === 'denied';

	return (
		<Prompt data-testid="desktop-notifications-prompt">
			<Icon use="notifications" size={1.25} color={isBlocked ? 'silver-base' : 'blue-base'} />

			<PromptBody>
				<PromptTitle>
					{ isBlocked ? 'Desktop notifications are blocked' : 'Turn on desktop notifications' }
				</PromptTitle>

				<PromptText>
					{ isBlocked
						? 'Open the site settings from the icon at the left of the address bar and'
							+ ' set Notifications to Allow. We can\'t ask for you once it has been blocked.'
						: 'Get alerted about orders, workflow and embargoes even when Pixwel is not the active tab.' }
				</PromptText>

				{ !isBlocked && (
					<Button
						variant="primary"
						size="sm"
						disabled={isRequesting}
						loading={isRequesting}
						onClick={handleEnable}
						data-testid="enable-desktop-notifications"
					>
						Enable notifications
					</Button>
				)}
			</PromptBody>

			<PromptDismiss
				type="button"
				aria-label="Dismiss"
				title="Dismiss"
				onClick={() => setDismissedAt(permission)}
				data-testid="dismiss-desktop-notifications"
			>
				<Icon use="close" size={0.625} color="silver-base" />
			</PromptDismiss>
		</Prompt>
	);
}

DesktopNotificationsPrompt.propTypes = {
	permission: PropTypes.string,
	isSupported: PropTypes.bool,
	isRequesting: PropTypes.bool,
	onEnable: PropTypes.func
};

export default DesktopNotificationsPrompt;
